// cspell:disable

import { registerIdPrefix } from "@redotech/redo-ids-registry";
import {
  Crockford32Options,
  crockford32StringFormat,
} from "@redotech/util/crockford32";
import * as z from "zod";

export type ID<T> = string & { __idType: T };

export interface Crockford32Format<T extends string> {
  read(value: T): bigint;
  write(value: bigint): T;
  test(value: string): value is T;
  schema: z.ZodType<T, string>;
  readonly prefix: string;
}

export function idFormat<T extends string>({
  prefix = "",
}: Crockford32Options = {}): Crockford32Format<T> {
  registerIdPrefix(prefix, "crockford32", "crockford32-id");
  const { read, write } = crockford32StringFormat({ prefix });
  const result: Crockford32Format<T> = {
    get prefix() {
      return prefix;
    },
    read(value: T): bigint {
      return read(value);
    },
    write(value: bigint): T {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return write(value) as T;
    },
    test(value: string): value is T {
      return !prefix || value.startsWith(prefix);
    },
    schema: z
      .custom<T>((value) => {
        return (
          typeof value === "bigint" ||
          typeof value === "number" ||
          (typeof value === "string" &&
            (value.startsWith(prefix) || /^\d+$/.test(value)))
        );
      })
      .transform((value) => {
        if (typeof value === "bigint") {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          return write(value) as T;
        }
        if (typeof value === "number") {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          return write(BigInt(value)) as T;
        }
        if (typeof value === "string" && /^\d+$/.test(value)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          return write(BigInt(value)) as T;
        }
        return value;
      })
      .meta({ annotation: "String" }) as z.ZodType<T, string>,
  };
  return result;
}

// redo-ids split: CRDB id formats (mongo helpers in @redotech/redo-ids-mongo).
